import { UXPContext } from '@core/context/Context';
import { hasValue, toDate } from '@utils';
import { format } from 'date-fns';
import { useCallback, useContext } from 'react';
export const DATE_FORMAT_MAP = {
    'Y/M/D': 'yyyy/M/d',
    'D/M/Y': 'd/M/yyyy',
    'n d, Y': 'MMM d, yyyy',
    'd-N-Y': 'd-MMMM-yyyy',
    'M/D/Y': 'M/d/yyyy',
    'D.M.Y': 'd.M.yyyy',
    'Y-M-D': 'yyyy-MM-dd'
};
export const TIME_FORMAT_MAP = {
    '12': 'h:mm a',
    '24': 'HH:mm'
};
export const DATETIME_FORMAT_MAP = {
    '12': 'h:mm:ss a',
    '24': 'HH:mm:ss'
};
export const DEFAULT_DATE_FORMAT = 'yyyy/M/d';
export const DEFAULT_TIME_FORMAT = 'HH:mm';
export const DEFAULT_DATETIME_FORMAT = 'HH:mm:ss';
const getDateFormat = (systemFormat, fallback = DEFAULT_DATE_FORMAT) => {
    return systemFormat ? DATE_FORMAT_MAP[systemFormat] || fallback : fallback;
};
const getTimeFormat = (systemFormat, fallback = DEFAULT_TIME_FORMAT, includeSeconds = false) => {
    if (!systemFormat)
        return fallback;
    return includeSeconds ? DATETIME_FORMAT_MAP[systemFormat] || fallback : TIME_FORMAT_MAP[systemFormat] || fallback;
};
const getDateFormatFromOptions = (options, system) => {
    var _a;
    if (typeof (options === null || options === void 0 ? void 0 : options.format) === 'string')
        return options.format;
    return getDateFormat(((_a = options === null || options === void 0 ? void 0 : options.format) === null || _a === void 0 ? void 0 : _a.systemDateFormat) || system.userDataFormat);
};
const getTimeFormatFromOptions = (options, system) => {
    var _a;
    if (typeof (options === null || options === void 0 ? void 0 : options.format) === 'string')
        return options.format;
    const fallback = (options === null || options === void 0 ? void 0 : options.includeSeconds) ? DEFAULT_DATETIME_FORMAT : DEFAULT_TIME_FORMAT;
    return getTimeFormat(((_a = options === null || options === void 0 ? void 0 : options.format) === null || _a === void 0 ? void 0 : _a.systemTimeFormat) || system.userTimeFormat, fallback, options === null || options === void 0 ? void 0 : options.includeSeconds);
};
const convertToTimezone = (date, offsetSeconds) => {
    // 0 is a real offset (UTC sites); only a missing offset keeps the browser time
    if (typeof offsetSeconds !== 'number' || !isFinite(offsetSeconds))
        return date;
    const offsetMs = offsetSeconds * 1000;
    const utcTime = date.getTime() + (date.getTimezoneOffset() * 60000);
    return new Date(utcTime + offsetMs);
};
const formatDT = (date, dateFnsFormat, options, system) => {
    var _a, _b;
    const dateObj = toDate(date);
    if (!dateObj)
        return '';
    const hasCustomOffset = hasValue((_a = options === null || options === void 0 ? void 0 : options.timezone) === null || _a === void 0 ? void 0 : _a.offset, true, true);
    if (((_b = options.timezone) === null || _b === void 0 ? void 0 : _b.useBrowserTime) && !hasCustomOffset) {
        return format(dateObj, dateFnsFormat);
    }
    const offsetSeconds = hasCustomOffset ? options.timezone.offset : system.userSiteTimeZoneOffsetInSeconds;
    const convertedDate = convertToTimezone(dateObj, offsetSeconds);
    return format(convertedDate, dateFnsFormat);
};
/**
 * Pure (context-free) version of useFormatters().formatDate - pass the system
 * format slice explicitly. Used by useFormatters and the export engine.
 */
export const formatDateValue = (date, options, system) => {
    var _a, _b;
    switch (options.type) {
        case 'date': return formatDT(date, getDateFormatFromOptions(options, system), options, system);
        case 'time': return formatDT(date, getTimeFormatFromOptions(options, system), options, system);
        case 'datetime':
        default: {
            const customFormat = typeof (options === null || options === void 0 ? void 0 : options.format) == 'string';
            const fmt = customFormat
                ? options === null || options === void 0 ? void 0 : options.format
                : `${getDateFormatFromOptions(options, system)} ${getTimeFormatFromOptions(options, system)}`;
            const formatted = formatDT(date, fmt, options, system);
            const siteOffset = system.userSiteTimeZoneOffsetInSeconds;
            const siteZone = !((_a = options === null || options === void 0 ? void 0 : options.timezone) === null || _a === void 0 ? void 0 : _a.useBrowserTime) && !hasValue((_b = options === null || options === void 0 ? void 0 : options.timezone) === null || _b === void 0 ? void 0 : _b.offset, true, true)
                && typeof siteOffset === 'number' && isFinite(siteOffset);
            // older core instances send no abbreviation (uxp ships to the CDN ahead of them) - anything but a non-empty string means no label
            const abbr = typeof system.userTimezoneAbbreviation === 'string' ? system.userTimezoneAbbreviation.trim() : '';
            const label = (options === null || options === void 0 ? void 0 : options.includeTimezone) !== false && siteZone && !customFormat ? abbr : '';
            return formatted && label ? `${formatted} (${label})` : formatted;
        }
    }
};
/**
 * @export
 * Hook for formatting dates, times, and datetimes with timezone conversion support.
 *
 * This hook returns a `formatDate` function that you can use to format values
 * depending on the `type` you provide (`date`, `time`, or `datetime`).
 *
 * It automatically applies:
 * - System-defined formats (from context) OR your custom format strings.
 * - Timezone conversion (site, browser, or custom offset).
 * - Optional seconds in time outputs.
 * - The site timezone abbreviation after datetimes (v4 parity; opt out with `includeTimezone: false`).
 *
 * @example
 * ```tsx
 * // Import the hook and get the formatDate function
 * import { useFormatters } from '@/hooks/useFormatters';
 *
 * const { formatDate } = useFormatters();
 * const now = new Date();
 * ```
 *
 * @example
 * ```tsx
 * // Format as date only
 * const date = formatDate(now, { type: 'date' });
 * ```
 *
 * @example
 * ```tsx
 * // Format as time only
 * const time = formatDate(now, { type: 'time' });
 * ```
 *
 * @example
 * ```tsx
 * // Format as date + time, with the site timezone abbreviation appended (e.g. "2026/9/23 09:23 (IST)")
 * const dateTime = formatDate(now, { type: 'datetime' });
 * ```
 *
 * @example
 * ```tsx
 * // Date + time without the timezone abbreviation
 * const noZone = formatDate(now, { type: 'datetime', includeTimezone: false });
 * ```
 *
 * @example
 * ```tsx
 * // Custom format string (date-fns format)
 * const custom = formatDate(now, { type: 'datetime', format: 'EEEE, MMMM do yyyy HH:mm' });
 * ```
 *
 * @example
 * ```tsx
 * // Include seconds in time format
 * const withSeconds = formatDate(now, { type: 'time', includeSeconds: true });
 * ```
 *
 * @example
 * ```tsx
 * // Convert using browser timezone
 * const browserTime = formatDate(now, {
 *   type: 'datetime',
 *   timezone: { useBrowserTime: true }
 * });
 * ```
 *
 * @example
 * ```tsx
 * // Convert using a specific offset (e.g., UTC+5:30 = 19800 seconds)
 * const specificOffset = formatDate(now, {
 *   type: 'datetime',
 *   timezone: { offset: 19800 }
 * });
 * ```
 */
export const useFormatters = () => {
    const context = useContext(UXPContext);
    const formatDate = useCallback((date, options) => {
        return formatDateValue(date, options, {
            userDataFormat: context.userDataFormat,
            userTimeFormat: context.userTimeFormat,
            userSiteTimeZoneOffsetInSeconds: context.userSiteTimeZoneOffsetInSeconds,
            userTimezoneAbbreviation: context.userTimezoneAbbreviation
        });
    }, [context.userDataFormat, context.userTimeFormat, context.userSiteTimeZoneOffsetInSeconds, context.userTimezoneAbbreviation]);
    return { formatDate };
};
